import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { hydrate } from './store.js';
import { platform } from './platform/index.js';
import './ui.css';

// What this device remembers is read before the app is imported, not merely before it is
// drawn: a screen may name its pages as it is loaded, and those names are in the language
// this device was set to. A shell keeps them where only a promise reaches, so the promise
// is kept here, once, and everything after it is plain.
await hydrate();
const { App } = await import('./app.js');
createRoot(document.getElementById('root')!).render(<StrictMode><App /></StrictMode>);

// The service worker caches the app itself, so it opens with no network at all. Registered
// from here rather than an inline script because the page's Content-Security-Policy allows no
// inline script — and it should not, for an app that holds keys. Only in a build: under
// `pnpm dev` there is no sw.js, and a stale worker would serve yesterday's bundle over Vite's.
// Only a browser has one, and only a browser needs one: a shell carries its assets in the
// bundle, and on iOS a page served from the app cannot register a worker at all.
if (platform.kind === 'web' && 'serviceWorker' in navigator && import.meta.env.PROD) {
  // Whether a worker was already running this page when it opened. Without one, the first
  // worker taking control is not an update — it is the app being installed — and a reload
  // then would be a reload for nothing.
  const wasControlled = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    // A new build has taken over. Nothing would be lost by reloading — everything a person
    // has written is in IndexedDB, outbox included — but a page that reloads itself under
    // someone's thumb is rude. The app is told, and decides: it reloads at once while nobody
    // is in the middle of anything, and otherwise offers.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (wasControlled) window.dispatchEvent(new CustomEvent('chilavubook:update-ready'));
    });
    // Look for a new build when the app comes back to the foreground, and on a slow tick for
    // a window left open: a browser checks the worker's own script only on a navigation or
    // once a day, which on an installed app that is never navigated is close to never.
    const look = (): void => { void reg.update().catch(() => {}); };
    document.addEventListener('visibilitychange', () => { if (!document.hidden) look(); });
    setInterval(look, 30 * 60_000);
    look();
  }).catch((e) => {
    // No worker: the app still runs, it just needs the network to open and cannot be woken.
    // Said in the console rather than swallowed, since it is the first place anyone looks.
    console.warn('Chilavu: the service worker did not install —', e);
  });
}
